/**
 * src/lib/agent/agentModeContext.jsx — F2 (2026-04-21)
 *
 * Single source of truth for which voice pipeline owns the mic and
 * the audio output. Prevents the 2026-04-21 "interrupted by voice to
 * speech" bug where the always-mounted VoiceModeOverlay kept its VAD
 * alive in the background and fired a conversation turn on top of
 * the user's dictation.
 *
 * Three modes, mutually exclusive:
 *   - 'idle'         nothing is using the mic or playing agent audio
 *   - 'conversation' full voice chat overlay is open; TTS may play;
 *                    VAD may auto-start turns
 *   - 'capture'      useDictation is running; single-shot STT into
 *                    the composer; NO agent audio, NO VAD-driven turns
 *
 * Usage:
 *   const { mode, enterMode, exitMode } = useAgentMode();
 *   enterMode('conversation')  → if currently 'capture', we implicitly
 *                                 exit capture first (caller is the last
 *                                 line of defense; downstream audio
 *                                 gates will also enforce).
 *   exitMode('conversation')   → no-op if current mode is not
 *                                 'conversation' (stale-call safe).
 *
 * Provider lives at app root (src/main.jsx) so every hook sees the
 * same mode regardless of which view is rendering.
 *
 * Downstream enforcement:
 *   - useStreamingTTS.speak() checks mode === 'conversation' before
 *     enqueuing (F3)
 *   - useDictation.start() calls enterMode('capture') which implicitly
 *     kicks an in-flight conversation (F4)
 *   - VoiceModeOverlay calls enterMode('conversation') on mount and
 *     exitMode('conversation') on unmount (F4)
 *
 * Observability: every transition emits a mode_transition event via
 * logVoiceEvent so the new audit trail shows exactly what produced
 * what and when. (F6)
 */

import React, { createContext, useCallback, useContext, useState } from 'react';
import { logVoiceEvent } from '../voice/voiceLogger';

export const AGENT_MODES = ['idle', 'conversation', 'capture'];

const AgentModeContext = createContext(null);

/**
 * Provider. Mount once at app root, wrap the tree.
 */
export function AgentModeProvider({ children }) {
  const [mode, setMode] = useState('idle');

  const enterMode = useCallback((next) => {
    if (!AGENT_MODES.includes(next)) {
      throw new Error(`[agentMode] Invalid mode: ${next}. Expected one of: ${AGENT_MODES.join(', ')}`);
    }
    if (next === 'idle') {
      // enterMode('idle') is semantic nonsense — callers should use exitMode.
      // Treat as no-op rather than throw to keep surface forgiving.
      return;
    }
    setMode((prev) => {
      if (prev === next) return prev;
      try {
        logVoiceEvent('mode_transition', 'ok', `agentMode: ${prev} → ${next}`, {
          from: prev, to: next, trigger: 'enterMode',
        });
      } catch (e) {
        // TICKET-053 (AEGIS LOW 2026-05-01) — fallback diagnostic so a
        // logVoiceEvent failure (Supabase down, app_error_logs gone) is
        // still visible somewhere. Never blocks the transition.
        // eslint-disable-next-line no-console
        console.warn('[agentMode] logVoiceEvent failed:', e?.message || e);
      }
      return next;
    });
  }, []);

  const exitMode = useCallback((which) => {
    setMode((prev) => {
      if (prev !== which) {
        // Stale exit — caller left the mode already, don't drag a live
        // session back to idle.
        return prev;
      }
      try {
        logVoiceEvent('mode_transition', 'ok', `agentMode: ${prev} → idle`, {
          from: prev, to: 'idle', trigger: 'exitMode',
        });
      } catch (e) {
        // TICKET-053 — same fallback as enterMode catch above.
        // eslint-disable-next-line no-console
        console.warn('[agentMode] logVoiceEvent failed:', e?.message || e);
      }
      return 'idle';
    });
  }, []);

  const value = { mode, enterMode, exitMode };
  return (
    <AgentModeContext.Provider value={value}>
      {children}
    </AgentModeContext.Provider>
  );
}

/**
 * Consumer hook. Safe to call outside a provider — returns a default
 * that reads as idle with no-op setters. This is deliberately forgiving
 * so legacy components that haven't been wrapped yet don't crash; the
 * F3 TTS gate + F4 exclusion logic are the enforcement layer, not this
 * hook.
 *
 * The fallback object is MODULE-CONSTANT so callers that use enterMode
 * or exitMode in useEffect deps / useCallback deps do not see a new
 * reference on every render. Without this, any hook that depends on
 * these setters would re-run its effects forever.
 */
const NOOP_FALLBACK = Object.freeze({
  mode: 'idle',
  enterMode: () => { /* no provider — ignored */ },
  exitMode: () => { /* no provider — ignored */ },
});

export function useAgentMode() {
  const ctx = useContext(AgentModeContext);
  return ctx || NOOP_FALLBACK;
}
